'use client';

import React from 'react';
import { LuShield } from 'react-icons/lu';
import { PiCheckCircleBold } from "react-icons/pi";
import Link from 'next/link';

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-linear-to-b from-gray-50 to-gray-100">
      {/* Hero Section */}
      <div className="relative h-[30vh] sm:h-[40vh] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/images/pages/privacy-policy-hero.jpg"
            alt="Privacy Policy"
            className="h-full w-full object-cover object-center brightness-[0.7]"
          />
          <div className="absolute inset-0 bg-linear-to-b from-[#101828]/80 via-[#101828]/70 to-transparent">
            {/* Add subtle pattern overlay */}
            <div className="absolute inset-0 opacity-20 bg-[url('/images/patterns/circuit-pattern.svg')] bg-repeat"></div>
          </div>
        </div>
        <div className="relative h-full mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-full flex-col items-center justify-center text-center">
            <div className="mb-4 rounded-full bg-[#00B0F0]/20 p-2 sm:p-3">
              <LuShield className="h-6 w-6 sm:h-8 sm:w-8 text-[#00B0F0]" />
            </div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white drop-shadow-lg">
              Privacy Policy
            </h1>
            <p className="mt-2 sm:mt-4 text-sm sm:text-base md:text-lg text-gray-200 max-w-2xl">
              How we collect, use, and protect your information
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8">
        {/* Last Updated Section */}
        <div className="mb-8 text-center">
          <p className="text-sm text-gray-500">Last Updated: April 2, 2025</p>
        </div>

        {/* Introduction */}
        <div className="mb-10 bg-white rounded-xl shadow-xs p-6 sm:p-8">
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-4">Introduction</h2>
          <p className="text-gray-600 mb-4">
            At Qwetulands, we respect your privacy and are committed to protecting your personal data. This privacy policy will inform you about how we look after your personal data when you visit our website and tell you about your privacy rights and how the law protects you.
          </p>
          <p className="text-gray-600">
            Please read this privacy policy carefully before using our services. By accessing or using our services, you agree to the collection and use of information in accordance with this policy.
          </p>
        </div>

        {/* Information We Collect */}
        <div className="mb-10 bg-white rounded-xl shadow-xs p-6 sm:p-8">
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-4">Information We Collect</h2>
          
          <h3 className="text-lg font-medium text-gray-700 mb-3">Personal Information</h3>
          <p className="text-gray-600 mb-4">
            We may collect personal identification information from users in various ways, including, but not limited to, when users visit our site, register on the site, fill out a form, and in connection with other activities, services, features, or resources we make available. Users may be asked for, as appropriate:
          </p>
          <ul className="list-none space-y-2 mb-6">
            {[
              'Name',
              'Email address',
              'Phone number',
              'Address',
              'Payment information (for services)',
            ].map((item) => (
              <li key={item} className="flex items-start">
                <PiCheckCircleBold className="h-5 w-5 text-[#00B0F0] mr-2 mt-0.5 shrink-0" />
                <span className="text-gray-600">{item}</span>
              </li>
            ))}
          </ul>

          <h3 className="text-lg font-medium text-gray-700 mb-3">Non-Personal Information</h3>
          <p className="text-gray-600 mb-4">
            We may collect non-personal identification information about users whenever they interact with our site. This may include:
          </p>
          <ul className="list-none space-y-2">
            {[
              'Browser name',
              'Type of computer or device',
              'Technical information about users\' means of connection to our site',
              'Operating system',
              'Internet service provider',
              'Other similar information',
            ].map((item) => (
              <li key={item} className="flex items-start">
                <PiCheckCircleBold className="h-5 w-5 text-[#00B0F0] mr-2 mt-0.5 shrink-0" />
                <span className="text-gray-600">{item}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* How We Use Your Information */}
        <div className="mb-10 bg-white rounded-xl shadow-xs p-6 sm:p-8">
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-4">How We Use Your Information</h2>
          <p className="text-gray-600 mb-4">
            Qwetulands may collect and use users' personal information for the following purposes:
          </p>
          <ul className="list-none space-y-3">
            {[
              'To improve customer service: Information you provide helps us respond to your customer service requests and support needs more efficiently.',
              'To personalize user experience: We may use information in the aggregate to understand how our users as a group use the services and resources provided on our site.',
              'To improve our site: We may use feedback you provide to improve our products and services.',
              'To process payments: We may use the information users provide about themselves when placing an order only to provide service to that order.',
              'To send periodic emails: We may use the email address to send user information and updates pertaining to their order. It may also be used to respond to their inquiries, questions, and/or other requests.',
            ].map((item, index) => (
              <li key={index} className="flex items-start">
                <PiCheckCircleBold className="h-5 w-5 text-[#00B0F0] mr-2 mt-0.5 shrink-0" />
                <span className="text-gray-600">{item}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* How We Protect Your Information */}
        <div className="mb-10 bg-white rounded-xl shadow-xs p-6 sm:p-8">
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-4">How We Protect Your Information</h2>
          <p className="text-gray-600 mb-4">
            We adopt appropriate data collection, storage, and processing practices and security measures to protect against unauthorized access, alteration, disclosure, or destruction of your personal information, username, password, transaction information, and data stored on our site.
          </p>
          <p className="text-gray-600">
            Sensitive and private data exchange between the site and its users happens over an SSL secured communication channel and is encrypted and protected with digital signatures.
          </p>
        </div>

        {/* Sharing Your Personal Information */}
        <div className="mb-10 bg-white rounded-xl shadow-xs p-6 sm:p-8">
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-4">Sharing Your Personal Information</h2>
          <p className="text-gray-600 mb-4">
            We do not sell, trade, or rent users' personal identification information to others. We may share generic aggregated demographic information not linked to any personal identification information regarding visitors and users with our business partners, trusted affiliates, and advertisers for the purposes outlined above.
          </p>
        </div>

        {/* Third-Party Websites */}
        <div className="mb-10 bg-white rounded-xl shadow-xs p-6 sm:p-8">
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-4">Third-Party Websites</h2>
          <p className="text-gray-600 mb-4">
            Users may find advertising or other content on our site that links to the sites and services of our partners, suppliers, advertisers, sponsors, licensors, and other third parties. We do not control the content or links that appear on these sites and are not responsible for the practices employed by websites linked to or from our site.
          </p>
          <p className="text-gray-600">
            Browsing and interaction on any other website, including websites which have a link to our site, is subject to that website's own terms and policies.
          </p>
        </div>

        {/* Changes to This Privacy Policy */}
        <div className="mb-10 bg-white rounded-xl shadow-xs p-6 sm:p-8">
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-4">Changes to This Privacy Policy</h2>
          <p className="text-gray-600 mb-4">
            Qwetulands has the discretion to update this privacy policy at any time. When we do, we will revise the updated date at the top of this page. We encourage users to frequently check this page for any changes to stay informed about how we are helping to protect the personal information we collect.
          </p>
          <p className="text-gray-600">
            You acknowledge and agree that it is your responsibility to review this privacy policy periodically and become aware of modifications.
          </p>
        </div>

        {/* Your Acceptance of These Terms */}
        <div className="mb-10 bg-white rounded-xl shadow-xs p-6 sm:p-8">
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-4">Your Acceptance of These Terms</h2>
          <p className="text-gray-600 mb-4">
            By using this site, you signify your acceptance of this policy. If you do not agree to this policy, please do not use our site. Your continued use of the site following the posting of changes to this policy will be deemed your acceptance of those changes.
          </p>
        </div>

        {/* Contact Us */}
        <div className="bg-white rounded-xl shadow-xs p-6 sm:p-8">
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-4">Contact Us</h2>
          <p className="text-gray-600 mb-4">
            If you have any questions about this Privacy Policy, the practices of this site, or your dealings with this site, please contact us at:
          </p>
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <p className="text-gray-700 font-medium">Qwetulands</p>
            <p className="text-gray-600">Malindi, Opposite Mbuzi Wengi</p>
            <p className="text-gray-600">Email: <a href="mailto:info@qwetulands.com" className="text-[#00B0F0] hover:underline">info@qwetulands.com</a></p>
            <p className="text-gray-600">Phone: <a href="tel:+254756211546" className="text-[#00B0F0] hover:underline">+254 756 211 546</a></p>
          </div>
        </div>

        {/* Back to Home Button */}
        <div className="mt-10 text-center">
          <Link 
            href="/"
            className="inline-flex items-center justify-center rounded-lg bg-[#00B0F0] px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-[#0090D0] focus:outline-hidden focus:ring-2 focus:ring-[#00B0F0] focus:ring-offset-2"
          >
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
